// U5 "Dictate a reply" (experiment): the browser's speech recognition, in Chrome only. Chrome's
// recognition sends the audio to Google's speech service, which "data goes only to Google and
// Anthropic" allows; Safari's goes to Apple (and Edge's to Microsoft), so neither is offered. The
// audio never reaches MeeMail's server; only the transcript the owner chooses to send does.

interface Brand {
  brand: string;
}

export interface NavigatorLike {
  userAgent: string;
  language?: string;
  userAgentData?: { brands?: readonly Brand[] } | undefined;
}

export type SpeechSupport = "chrome" | "not_chrome" | "no_api";

interface RecognitionResultLike {
  isFinal: boolean;
  readonly length: number;
  [index: number]: { transcript: string } | undefined;
}

interface RecognitionEventLike {
  resultIndex: number;
  results: { readonly length: number; [index: number]: RecognitionResultLike | undefined };
}

interface RecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: RecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type RecognitionCtor = new () => RecognitionLike;

function recognitionCtor(win: object): RecognitionCtor | null {
  const w = win as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Google Chrome itself (not Edge, Opera, Brave, Samsung Internet, Safari or headless Chromium). */
export function isChrome(nav: NavigatorLike): boolean {
  const brands = nav.userAgentData?.brands;
  if (brands?.length) return brands.some((b) => b.brand === "Google Chrome");
  const ua = nav.userAgent;
  return (
    /\bChrome\/\d+/.test(ua) &&
    !/\b(Edg|EdgA|EdgiOS|OPR|Opera|SamsungBrowser|YaBrowser|Vivaldi|HeadlessChrome|Brave)\b/.test(
      ua,
    )
  );
}

export function speechSupport(nav: NavigatorLike, win: object): SpeechSupport {
  if (!isChrome(nav)) return "not_chrome";
  return recognitionCtor(win) ? "chrome" : "no_api";
}

/** The browser's own answer, for components. */
export function browserSpeechSupport(): SpeechSupport {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "no_api";
  return speechSupport(navigator as NavigatorLike, window);
}

/** Final words so far plus the words still being heard. */
export function readTranscript(event: RecognitionEventLike): { final: string; interim: string } {
  let final = "";
  let interim = "";
  for (let i = 0; i < event.results.length; i++) {
    const r = event.results[i];
    const words = r?.[0]?.transcript ?? "";
    if (r?.isFinal) final += words;
    else interim += words;
  }
  return { final: final.replace(/\s+/g, " ").trim(), interim: interim.replace(/\s+/g, " ").trim() };
}

export interface Listener {
  stop(): void;
  abort(): void;
}

export interface ListenHandlers {
  onTranscript: (t: { final: string; interim: string }) => void;
  onEnd: () => void;
  onError: (error: string) => void;
}

/** Starts listening; null when the browser has no recognition. */
export function listen(handlers: ListenHandlers, win: object = window): Listener | null {
  const Ctor = recognitionCtor(win);
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
  rec.onresult = (e) => handlers.onTranscript(readTranscript(e));
  rec.onerror = (e) => handlers.onError(e.error ?? "error");
  rec.onend = () => handlers.onEnd();
  rec.start();
  return { stop: () => rec.stop(), abort: () => rec.abort() };
}

/** Plain words for a recognition error ("not-allowed": the microphone was refused). */
export function dictationErrorWords(error: string): string {
  if (error === "not-allowed" || error === "service-not-allowed")
    return "Chrome wasn't allowed to use the microphone. Nothing was sent.";
  if (error === "no-speech") return "Didn't hear anything. Nothing was sent.";
  if (error === "network") return "Chrome couldn't reach its speech service. Nothing was sent.";
  return "Dictation stopped. Nothing was sent.";
}
